import clsx from 'clsx'
import isEmpty from 'lodash/isEmpty'
import toNumber from 'lodash/toNumber'
import React, { FC, ReactNode, useContext, useMemo, useState } from 'react'

import { NativeModal, NativeModalBody, NativeModalHeader } from '@Components/Modal/NativeModal/NativeModal'
import { PaymentIcon } from '@Domains/Checkout/Steps/Payment/Components/PaymentIconFactory/PaymentIconFactory'
import { PageType } from '@Generated/graphql'
import { useModalComponents } from '@Globals/CMS/Modal/Content'
import { useTranslationMap } from '@Globals/Hooks/useTranslationMap'
import { MetadataContext } from '@Globals/Metadata'

import styles from './PayLaterInfo.module.scss'

export interface PayLaterInfoProps{
  className?: string
}

export const PayLaterInfo: FC<PayLaterInfoProps> = ({ className }) => {
  const { t, tComponent } = useTranslationMap()
  const { store } = useContext(MetadataContext)
  const [componentId, setComponentId] = useState<string>('')
  const modalComponents = useModalComponents(PageType.Modal, componentId)
  const payLaterWithPaypal = [28, 7, 8, 27, 36].includes(toNumber(store.id))
  const payLaterWithWalley = [4, 3, 6].includes(toNumber(store.id))
  const payLaterWithKlarna = [18, 7, 6, 5, 3, 4, 8].includes(toNumber(store.id))
  const payLaterWithByJuno = [19, 20].includes(toNumber(store.id))

  const components = useMemo(() => {
    const payPalIco: ReactNode = payLaterWithPaypal
      ? <PaymentIcon key="payLaterInfo_paypal" className={styles.payLaterIcon} name="paypal" url="https://eu-images.contentstack.com/v3/assets/blt7dcd2cfbc90d45de/bltbcce05855f9b83e3/paypal.svg" />
      : null
    const klarnaIco = payLaterWithKlarna
      ? <PaymentIcon key="payLaterInfo_klarna_v2" className={styles.payLaterIcon} name="klarna_v2" url="https://eu-images.contentstack.com/v3/assets/blt7dcd2cfbc90d45de/bltcfd098f9960fe2b3/klarna_ico.svg" />
      : null
    const byJunoIco: ReactNode = payLaterWithByJuno
      ? <PaymentIcon key="payLaterInfo_cembrapay" className={styles.payLaterIcon} name="cembrapay" url="https://eu-images.contentstack.com/v3/assets/blt7dcd2cfbc90d45de/bltdba14d63bfb9a6ec/cembrapay.svg" />
      : null
    const walleyIco = payLaterWithWalley
      ? <PaymentIcon key="payLaterInfo_walley" className={styles.payLaterIcon} name="walley" url="https://eu-images.contentstack.com/v3/assets/blt7dcd2cfbc90d45de/bltfeb124271dfeb2b3/walley.svg" />
      : null

    const getLearnMoreButton = (modalId: string = '33') => (
      <button
        key="payLaterInfo_button33"
        className={styles.learnMore}
        onClick={() => setComponentId(modalId)}
        type="button"
      >
        {t('products:PAY_LATER_LEARN_MORE')}
      </button>
    )

    const availablePayLaterOptions = [payPalIco, klarnaIco, byJunoIco, walleyIco].filter(a => !!a)

    let modalId: string = '33'
    if (availablePayLaterOptions.length === 1 && payLaterWithPaypal) {
      modalId = '32'
    } else if (availablePayLaterOptions.length === 1 && payLaterWithKlarna) {
      modalId = '31'
    }

    if (availablePayLaterOptions.length === 1) {
      return tComponent('products:PAY_LATER_ONE_OPTION', {
        arg1: availablePayLaterOptions[0],
        arg2: getLearnMoreButton(modalId),
      })
    }
    if (availablePayLaterOptions.length === 2) {
      return tComponent('products:PAY_LATER_TWO_OPTIONS', {
        arg1: availablePayLaterOptions[0],
        arg2: availablePayLaterOptions[1],
        arg3: getLearnMoreButton(),
      })
    }
    return tComponent('products:PAY_LATER_ALL', {
      arg1: getLearnMoreButton(),
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payLaterWithByJuno, payLaterWithKlarna, payLaterWithPaypal, payLaterWithWalley, tComponent])

  if (!payLaterWithPaypal && !payLaterWithWalley && !payLaterWithKlarna && !payLaterWithByJuno) {
    return null
  }

  return (
    <>
      <span className={clsx(styles.payLaterInfo, className)}>
      {...components}
      </span>
      {!isEmpty(componentId) && (
        <NativeModal backdrop closeOnBackDrop defaultOpen={!!modalComponents} onClose={() => setComponentId('')}>
          <NativeModalHeader />
          <NativeModalBody>
            {modalComponents}
          </NativeModalBody>
        </NativeModal>
      )}
    </>
  )
}
